import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';

const EMPTY = { currentPassword: '', newPassword: '', confirmPassword: '' };

/**
 * Change the signed-in user's password. New logins land here after signing in
 * with the temporary password they were emailed, and cannot leave until they
 * choose their own (8866); anyone else reaches it from the account menu.
 */
export default function ChangePasswordPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const forced = Boolean(user?.must_change_password);

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setFormError('');
    setDone(false);
  };

  const validate = () => {
    const next = {};
    if (!form.currentPassword) next.currentPassword = 'Enter your current password';
    if (form.newPassword.length < 8) next.newPassword = 'The new password must be at least 8 characters';
    else if (form.newPassword === form.currentPassword)
      next.newPassword = 'The new password must be different from the current one';
    if (form.confirmPassword !== form.newPassword) next.confirmPassword = 'The passwords do not match';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const fresh = await api.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      updateUser({ ...user, ...fresh });
      setForm(EMPTY);
      if (forced) navigate('/dashboard', { replace: true });
      else setDone(true);
    } catch (err) {
      setFormError(err.message || 'Failed to change the password.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, autoComplete) => (
    <div className="mb-3">
      <label htmlFor={`cp-${key}`} className="form-label small fw-bold text-muted">
        {label} <span className="text-danger">*</span>
      </label>
      <input
        id={`cp-${key}`}
        type="password"
        autoComplete={autoComplete}
        className={`form-control form-control-sm${errors[key] ? ' is-invalid' : ''}`}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
        data-testid={`cp-${key}`}
      />
      {errors[key] && <div className="invalid-feedback">{errors[key]}</div>}
    </div>
  );

  return (
    <div className="container-fluid py-4" data-testid="change-password-page" style={{ maxWidth: 520 }}>
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h5 className="text-primary mb-1">
            <i className="fas fa-key me-2" aria-hidden="true" />
            Change Password
          </h5>
          {forced ? (
            <div className="alert alert-warning py-2 small mt-3" data-testid="cp-required">
              You signed in with a temporary password. Choose your own password to continue.
            </div>
          ) : (
            <p className="text-muted small mb-3">Choose a new password for {user?.email}.</p>
          )}
          {formError && (
            <div className="alert alert-danger py-2 small" role="alert" data-testid="cp-error">
              {formError}
            </div>
          )}
          {done && (
            <div className="alert alert-success py-2 small" data-testid="cp-success">
              Your password has been changed.
            </div>
          )}
          <form onSubmit={submit} noValidate>
            {field('currentPassword', forced ? 'Temporary password' : 'Current password', 'current-password')}
            {field('newPassword', 'New password', 'new-password')}
            {field('confirmPassword', 'Confirm new password', 'new-password')}
            <button className="btn btn-primary w-100" disabled={saving} data-testid="cp-submit">
              {saving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
              Change Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
